import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FILTER_REGISTRY,
  readFilterParams,
  writeFilterParams,
  type FilterKey,
  type FilterValues,
} from '@attendance/shared';
import { URL_SEARCH_DEBOUNCE_MS } from '../lib/useUrlSearchTerm.js';

/* ===========================================================================
 * A LIST'S STATE LIVES IN ITS ADDRESS — search, filters, time, through one hook.
 *
 * Fully URL-backed before this: Employees, the attendance calendar, People,
 * reports, worker history. Not: dispatch, scan approvals, corrections, leave,
 * team attendance, devices, the worksite list, schedule, ERP, and every
 * `useClientControls` popover. So Back from a worker lost Tomorrow, a shared
 * link opened a different queue, and the search text could survive a reload
 * while the filters beside it did not (action-bar plan §2, item 7).
 *
 * A screen names the registry keys it offers; the registry
 * (packages/shared/src/filters/registry.ts) owns the URL key, the zod, and the
 * legacy spellings. This hook only moves values between the two:
 *
 *   - writes with `replace`, so a filter change is not a history entry — Back
 *     leaves the LIST, it does not undo a filter;
 *   - drops a default (absent / empty) instead of writing `?site=`;
 *   - reads a refused value as absent, so a tampered link shows the whole list;
 *   - never touches a param it does not own (`view`, `employeeId`, a page's
 *     own keys) — the registry's `writeFilterParams` guarantees that;
 *   - the SEARCH BOX answers at once and the address catches up after
 *     `URL_SEARCH_DEBOUNCE_MS` (250 ms, the app's one figure), exactly as
 *     `useUrlSearchTerm` does; an address changed from outside (Back, a link)
 *     wins over the box.
 *
 * A SCREEN'S OWN OLD KEYS (`aliases`). A registry legacy key may not be
 * another filter's canonical key, so a screen that once wrote a key it no
 * longer means — the scan queue's `?site=` for where the scan happened, now
 * the registry's `at` — names it here. It is read when the canonical key is
 * absent, and dropped on the next write, exactly like a registry legacy key,
 * but only on that screen.
 *
 * A PAGED LIST NAMES ITS PAGE KEY (`resets`). Page 4 of "everyone" is not page
 * 4 of "Civil": narrowing usually shortens the list, so the old page is an
 * empty screen that reads as "everybody vanished". Every key in `resets` is
 * dropped by the SAME write that changes a filter — one write, so it cannot
 * race a second `setSearchParams` in the same tick.
 * ======================================================================== */

/** Screen-local older URL keys per registry key: read, never written. */
export type ScreenAliases<K extends FilterKey> = Partial<Record<K, readonly string[]>>;

/** `params` with each alias moved onto its canonical key — when that key is not already there. */
export function withAliases<K extends FilterKey>(params: URLSearchParams, aliases: ScreenAliases<K>): URLSearchParams {
  const entries = Object.entries(aliases) as [K, readonly string[]][];
  if (entries.length === 0) return params;
  const out = new URLSearchParams(params);
  for (const [key, olds] of entries) {
    const canonical = FILTER_REGISTRY[key].urlKey;
    for (const old of olds) {
      const value = out.get(old);
      out.delete(old);
      if (value !== null && !out.has(canonical)) out.set(canonical, value);
    }
  }
  return out;
}

/** What `useListUrlState` reads from an address — the same read, for a caller outside React (a link builder, a test). */
export function readListUrl<K extends FilterKey>(
  params: URLSearchParams,
  keys: readonly K[],
  aliases: ScreenAliases<K> = {}
): Partial<Record<K, string>> {
  return readFilterParams(withAliases(params, aliases), keys) as Partial<Record<K, string>>;
}

export interface ListUrlState<K extends FilterKey> {
  /** The values in the address, by key — refused or absent values are missing. */
  values: Partial<Record<K, string>>;
  /** Set (or, with `undefined` / `''`, clear) some keys. Others are kept. */
  set: (patch: Partial<Record<K, string | undefined>>, own?: ScreenParams) => void;
  /** Clear these keys — every key this screen offers when called bare. */
  clear: (keys?: readonly K[], own?: ScreenParams) => void;
  /** The search box's text, as typed (only when the screen offers `q`). */
  search: string;
  setSearch: (text: string) => void;
  /** How many keys other than `q` narrow — the popover badge's count. */
  filterCount: number;
  /** Anything narrows, the search included. */
  isNarrowed: boolean;
}

/**
 * A screen's OWN params (not registry keys) to write in the same write — a
 * section switch that also clears the filters. `null` deletes. One write, so
 * it cannot race a second `setSearchParams` in the same tick.
 */
export type ScreenParams = Readonly<Record<string, string | null>>;

export interface ListUrlOptions {
  /** Screen keys every filter or search change drops — a paged list's `page`. */
  resets?: readonly string[];
}

export function useListUrlState<K extends FilterKey>(
  keys: readonly K[],
  aliases: ScreenAliases<K> = {},
  options: ListUrlOptions = {}
): ListUrlState<K> {
  const [params, setParams] = useSearchParams();
  const signature = keys.join(',');
  const aliasSignature = JSON.stringify(aliases);
  // The arrays a caller writes inline are new on every render; their CONTENTS are the dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableKeys = useMemo(() => [...keys], [signature]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableAliases = useMemo(() => aliases, [aliasSignature]);
  const resetSignature = (options.resets ?? []).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resets = useMemo(() => [...(options.resets ?? [])], [resetSignature]);
  const values = useMemo(() => readListUrl(params, stableKeys, stableAliases), [params, stableKeys, stableAliases]);

  /* EVERY WRITE STARTS FROM THE LATEST ADDRESS, not the render's. React
     Router's functional `setSearchParams` hands over the params of the render
     that created it, so the search box's settle — a closure 250 ms old —
     wrote back the address as it was when the reader typed: a filter picked,
     or a section opened, inside that window was silently undone. `latest`
     follows every render AND every write of this hook, so a timer, and two
     writes in one tick, both compose. */
  const latest = useRef(params);
  latest.current = params;
  const set = useCallback(
    (patch: Partial<Record<K, string | undefined>>, own: ScreenParams = {}) => {
      const current = withAliases(latest.current, stableAliases);
      const next = writeFilterParams(current, { ...readFilterParams(current, stableKeys), ...patch } as FilterValues, stableKeys);
      for (const key of resets) next.delete(key);
      for (const [key, value] of Object.entries(own)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      latest.current = next;
      setParams(next, { replace: true });
    },
    [setParams, stableKeys, stableAliases, resets]
  );

  /* THE SEARCH BOX — local text, the address after a settle. */
  const offersSearch = stableKeys.includes('q' as K);
  const fromUrl = offersSearch ? (values['q' as K] ?? '') : '';
  const [search, setLocal] = useState(fromUrl);
  const written = useRef(fromUrl);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (fromUrl === written.current) return;
    written.current = fromUrl;
    setLocal(fromUrl);
  }, [fromUrl]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const setSearch = useCallback(
    (text: string) => {
      setLocal(text);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        written.current = text.trim();
        set({ q: text } as Partial<Record<K, string>>);
      }, URL_SEARCH_DEBOUNCE_MS);
    },
    [set]
  );

  const clear = useCallback(
    (only?: readonly K[], own?: ScreenParams) => {
      const cleared = only ?? stableKeys;
      if (cleared.includes('q' as K)) {
        clearTimeout(timer.current);
        written.current = '';
        setLocal('');
      }
      set(Object.fromEntries(cleared.map((k) => [k, undefined])) as Partial<Record<K, undefined>>, own);
    },
    [set, stableKeys]
  );

  const filterCount = stableKeys.filter((k) => k !== 'q' && values[k] !== undefined).length;
  return {
    values,
    set,
    clear,
    search,
    setSearch,
    filterCount,
    isNarrowed: filterCount > 0 || search.trim() !== '',
  };
}
